import { useEffect, useState } from "react";
import { useGetIdentity, useGetList, useGetOne } from "ra-core";
import { useLocation, useSearchParams } from "react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { LOAD_STATUS_CHOICES } from "../loads/LoadInputs";
import type { Company } from "../types";
import { DashboardLoadCard, type DashboardLoad } from "./DashboardLoadCard";
import { LoadWorkbenchDetail } from "./LoadWorkbenchDetail";
import {
  buildWorkbenchQuery,
  getWorkbenchDates,
  readWorkbenchFilters,
  writeWorkbenchFilters,
  type WorkbenchFilters,
} from "./loadWorkbenchUtils";

import { WorkbenchSavedViews } from "./WorkbenchSavedViews";
const statusOptions = [
  { id: "active", name: "Active loads" },
  { id: "holds", name: "On hold / SOS" },
  { id: "all", name: "All statuses" },
  ...LOAD_STATUS_CHOICES,
];
const periodOptions = [
  { id: "last-week", name: "Last week" },
  { id: "this-week", name: "This week" },
  { id: "next-week", name: "Next week" },
  { id: "this-month", name: "This month" },
  { id: "custom", name: "Custom dates" },
];

const FilterSelect = ({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { id: string; name: string }[];
  onChange: (value: string) => void;
}) => (
  <div className="min-w-0 space-y-1.5">
    <span className="text-xs font-medium text-muted-foreground">{label}</span>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full" aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.id} value={option.id}>
            {option.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const AccountFilter = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search), 250);
    return () => clearTimeout(timer);
  }, [search]);
  const { data = [], error } = useGetList<Company>("companies", {
    pagination: { page: 1, perPage: 30 },
    sort: { field: "name", order: "ASC" },
    filter: query ? { "name@ilike": `%${query}%` } : {},
  });
  const selected = useGetOne<Company>(
    "companies",
    { id: value },
    { enabled: !!value },
  );
  const options = [
    { id: "all", name: "All accounts" },
    ...data.map(({ id, name }) => ({ id: String(id), name })),
  ];
  if (value && !options.some(({ id }) => id === value))
    options.push({
      id: value,
      name: selected.data?.name || `Account ${value}`,
    });
  return (
    <div className="space-y-2">
      <Input
        aria-label="Search accounts"
        placeholder="Search accounts…"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      <FilterSelect
        label="Vendor or customer"
        value={value || "all"}
        options={options}
        onChange={(value) => onChange(value === "all" ? "" : value)}
      />
      {error && (
        <p role="alert" className="text-xs text-destructive">
          Unable to search accounts.
        </p>
      )}
    </div>
  );
};

export const LoadWorkbench = () => {
  const { identity, isPending: identityPending } = useGetIdentity();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const isMobile = useIsMobile();
  const rawFilters = readWorkbenchFilters(params);
  const filters = {
    ...rawFilters,
    status: statusOptions.some(({ id }) => id === rawFilters.status)
      ? rawFilters.status
      : "active",
  };
  const dates = getWorkbenchDates(filters);
  const page = Math.max(
    1,
    Math.min(100000, Math.floor(Number(params.get("loadPage"))) || 1),
  );
  const selectedId = /^\d+$/.test(params.get("load") || "")
    ? params.get("load")
    : null;
  const {
    data = [],
    total,
    isPending,
    isFetching,
    error,
  } = useGetList<DashboardLoad>(
    "loads_summary",
    {
      pagination: { page, perPage: 20 },
      sort: { field: "pickup_date", order: "DESC" },
      filter: buildWorkbenchQuery(
        filters,
        identity?.id ?? "",
        LOAD_STATUS_CHOICES.map(({ id }) => id),
      ),
    },
    { enabled: !!identity?.id && !!dates },
  );
  const updateFilters = (patch: Partial<WorkbenchFilters>) => {
    setParams(
      (previous) => writeWorkbenchFilters(previous, { ...filters, ...patch }),
      { preventScrollReset: true },
    );
  };
  const updateParam = (key: string, value: string) =>
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { preventScrollReset: true },
    );

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <CardHeader className="border-b py-5">
        <CardTitle>My load workbench</CardTitle>
        <CardDescription>
          Follow your accounts and loads without opening a separate tab for
          every record. Periods use pickup date; weeks run Monday–Sunday.
        </CardDescription>
      </CardHeader>
      <div className="space-y-4 border-b p-4 sm:p-5">
        <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <FilterSelect
            label="My role"
            value={filters.role}
            options={[
              { id: "sales", name: "Sales owner" },
              { id: "purchasing", name: "Purchasing owner" },
              { id: "logistics", name: "Logistics owner" },
            ]}
            onChange={(role) =>
              updateFilters({ role: role as WorkbenchFilters["role"] })
            }
          />
          <FilterSelect
            label="Pickup period"
            value={filters.period}
            options={periodOptions}
            onChange={(period) =>
              updateFilters({ period: period as WorkbenchFilters["period"] })
            }
          />
          <FilterSelect
            label="Status"
            value={filters.status}
            options={statusOptions}
            onChange={(status) => updateFilters({ status })}
          />
          <AccountFilter
            value={filters.account}
            onChange={(account) => updateFilters({ account })}
          />
        </div>
        {filters.period === "custom" && (
          <div className="flex flex-wrap gap-3">
            <label className="space-y-1 text-xs">
              From
              <Input
                type="date"
                value={filters.from}
                onChange={(event) =>
                  updateFilters({ from: event.target.value })
                }
              />
            </label>
            <label className="space-y-1 text-xs">
              To
              <Input
                type="date"
                value={filters.to}
                onChange={(event) => updateFilters({ to: event.target.value })}
              />
            </label>
          </div>
        )}
        <WorkbenchSavedViews
          userId={identity?.id}
          filters={filters}
          valid={!!dates}
          onSelect={updateFilters}
        />
      </div>
      <div className="grid min-w-0 md:grid-cols-[minmax(280px,0.85fr)_minmax(0,1.5fr)]">
        <div className="min-w-0 md:border-r">
          <div className="flex items-center justify-between border-b px-4 py-3 text-sm">
            <span>{total == null ? "Your loads" : `${total} loads`}</span>
            <span className="text-xs text-muted-foreground">
              {dates ? `${dates.from} – ${dates.to}` : "Custom dates"}
            </span>
          </div>
          <div
            className="max-h-[75vh] space-y-3 overflow-y-auto p-3"
            aria-label="Load list"
            aria-busy={isFetching}
          >
            {!dates ? (
              <p role="alert" className="p-4 text-sm">
                Choose a valid start and end date.
              </p>
            ) : error || (!identityPending && !identity?.id) ? (
              <div role="alert" className="p-4 text-sm text-destructive">
                Unable to load your loads. Please refresh or sign in again.
              </div>
            ) : identityPending || isPending ? (
              <p className="p-4 text-sm text-muted-foreground">
                Loading loads…
              </p>
            ) : !data.length ? (
              <p className="p-4 text-sm text-muted-foreground">
                No loads match these filters.
              </p>
            ) : (
              data.map((load) => {
                const next = new URLSearchParams(params);
                next.set("load", String(load.id));
                return (
                  <DashboardLoadCard
                    key={load.id}
                    load={load}
                    selected={String(load.id) === selectedId}
                    to={`${location.pathname}?${next}`}
                  />
                );
              })
            )}
          </div>
          <div className="flex items-center justify-between gap-2 border-t p-3">
            <Button
              size="icon"
              variant="outline"
              aria-label="Previous loads page"
              disabled={page <= 1 || isFetching}
              onClick={() => updateParam("loadPage", String(page - 1))}
            >
              <ChevronLeft />
            </Button>
            <span className="text-xs text-muted-foreground">
              Page {page}
              {total != null ? ` of ${Math.max(1, Math.ceil(total / 20))}` : ""}
            </span>
            <Button
              size="icon"
              variant="outline"
              aria-label="Next loads page"
              disabled={
                isFetching ||
                !dates ||
                (total != null ? page * 20 >= total : data.length < 20)
              }
              onClick={() => updateParam("loadPage", String(page + 1))}
            >
              <ChevronRight />
            </Button>
          </div>
        </div>
        {!isMobile && (
          <aside
            className="max-h-[85vh] min-w-0 overflow-y-auto"
            aria-label="Selected load details"
          >
            {selectedId ? (
              <LoadWorkbenchDetail key={selectedId} id={selectedId} />
            ) : (
              <div className="flex min-h-72 items-center justify-center p-8 text-center text-sm text-muted-foreground">
                Select a load on the left to see details and available workflow
                actions.
              </div>
            )}
          </aside>
        )}
      </div>
      {isMobile && (
        <Sheet
          open={!!selectedId}
          onOpenChange={(open) => {
            if (!open) updateParam("load", "");
          }}
        >
          <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
            <SheetHeader>
              <SheetTitle>Load details</SheetTitle>
              <SheetDescription>
                Review the load and its available workflow actions.
              </SheetDescription>
            </SheetHeader>
            {selectedId && (
              <LoadWorkbenchDetail key={selectedId} id={selectedId} />
            )}
          </SheetContent>
        </Sheet>
      )}
    </Card>
  );
};
